import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent,
    Button, TextField, Box, Typography, IconButton,
    Stack, CircularProgress, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RocketLaunch as RocketLaunchIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

interface BookDemoModalProps {
    open: boolean;
    onClose: () => void;
}

const BookDemoModal: React.FC<BookDemoModalProps> = ({ open, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        schoolName: '',
        email: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(getApiUrl('auth/book-demo'), formData);
            setSuccess(true);
        } catch (error) {
            console.error('Failed to book demo:', error);
            alert('Failed to send demo request. Please try again or WhatsApp us directly.');
        } finally {
            setLoading(false);
        }
    };

    const handleWhatsApp = () => {
        const message = encodeURIComponent(`Hello SquareEdge Team, I would like to book a demo for ${formData.schoolName || 'my school'}. My name is ${formData.name}.`);
        window.open(`https://wa.me/263772690161?text=${message}`, '_blank');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    bgcolor: '#121926',
                    color: 'white',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))'
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <RocketLaunchIcon sx={{ color: '#ffab00' }} /> Book a Demo
                </Typography>
                <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)', py: 3 }}>
                {success ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h4" sx={{ mb: 2 }}>🎉</Typography>
                        <Typography variant="h6" fontWeight={700} gutterBottom>Demo Requested!</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.6)">
                            Our team will contact you within 24 hours to schedule your personalized walkthrough of SquareEdge.
                        </Typography>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={onClose}
                            sx={{ mt: 4, borderRadius: '12px', borderColor: '#ffab00', color: '#ffab00' }}
                        >
                            Got it
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<WhatsAppIcon />}
                            onClick={handleWhatsApp}
                            sx={{ mt: 2, borderRadius: '12px', bgcolor: '#25D366', color: 'white', fontWeight: 700, '&:hover': { bgcolor: '#128C7E' } }}
                        >
                            WhatsApp Us Directly
                        </Button>
                    </Box>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            <Typography variant="body2" color="rgba(255,255,255,0.6)">
                                Fill in the details below and experience the future of school financial management.
                            </Typography>

                            <TextField
                                fullWidth
                                label="Full Name"
                                variant="outlined"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                sx={{
                                    '& .MuiOutlinedInput-root': { color: 'white', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)' },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="School / Institution Name"
                                variant="outlined"
                                required
                                value={formData.schoolName}
                                onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                                sx={{
                                    '& .MuiOutlinedInput-root': { color: 'white', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)' },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Work Email Address"
                                type="email"
                                variant="outlined"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                sx={{
                                    '& .MuiOutlinedInput-root': { color: 'white', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)' },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Phone Number"
                                variant="outlined"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                sx={{
                                    '& .MuiOutlinedInput-root': { color: 'white', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)' },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    py: 2,
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    bgcolor: '#ffab00',
                                    color: 'black',
                                    '&:hover': { bgcolor: '#ffca28' },
                                    '&.Mui-disabled': { bgcolor: 'rgba(255,171,0,0.3)' }
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Demo Request'}
                            </Button>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }}>
                                <Typography variant="caption" color="rgba(255,255,255,0.3)">OR</Typography>
                            </Divider>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<WhatsAppIcon />}
                                onClick={handleWhatsApp}
                                sx={{
                                    py: 1.5,
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    borderColor: '#25D366',
                                    color: '#25D366',
                                    '&:hover': { borderColor: '#128C7E', bgcolor: 'rgba(37, 211, 102, 0.05)' }
                                }}
                            >
                                Chat on WhatsApp
                            </Button>
                        </Stack>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default BookDemoModal;
